import React, { useCallback } from 'react';
import merge from 'classnames';

import { IconCheckMark } from '@/components/icons/IconCheckMark';
import { getConfirmationIndex } from '@/components/modals/SubmitOrder/helpers';

import { HeaderStep, StepperHeaderProps as Props } from './types';

import styles from './Stepper.module.scss';

export const StepperHeader = ({ className, goToStep, steps, submitInProgress }: Props) => {
  const confirmationIndexStep = getConfirmationIndex(steps);
  const isConfirmationStepCompleted = steps[confirmationIndexStep]?.isStepComplete;

  const isCompletedStep = (index: number) => {
    const activeStepIndex = steps.findIndex(({ isActive }) => isActive);

    return activeStepIndex > index;
  };

  const isCompletedStepOrConfirmationStep = (index: number) =>
    isCompletedStep(index) || isConfirmationStepCompleted;

  const notHiddenSteps = steps.filter(stepItem => !stepItem.isHidden);
  const goToStepForm = useCallback(
    (stepItem: HeaderStep, index: number) => {
      // validate if the step was completed by completing the information and pressing the button to go to the next step
      // if we reach to the confirmation step, we check if all the previous steps are completed so we can navigate back
      // then validate if the information was confirmed, we mark that step as completed and we disable the navigation
      const isPrevStepCompleted = index > 0 && notHiddenSteps[index - 1].isStepComplete;
      const isCurrentStepCompleted = stepItem?.isStepComplete;

      if ((isCurrentStepCompleted || isPrevStepCompleted) && !isConfirmationStepCompleted) {
        goToStep?.(stepItem.step);
      }
    },
    [goToStep, isConfirmationStepCompleted, notHiddenSteps],
  );

  return (
    <div className={merge(styles.stepper_header, className)} data-testid="stepper-header">
      {notHiddenSteps.map((stepItem, index) => {
        const { step, label, isActive } = stepItem;

        return (
          <button
            className={styles.step_wrapper}
            disabled={submitInProgress}
            key={step}
            onClick={() => goToStepForm(stepItem, index)}
            type="button"
          >
            <div>
              <div
                className={merge(styles.step, {
                  [styles.active_step]: isActive,
                  [styles.completed_step]: isCompletedStepOrConfirmationStep(index),
                })}
              >
                {isCompletedStepOrConfirmationStep(index) ? (
                  <div className={styles.checkmark_wrapper}>
                    <IconCheckMark className={styles.checkmark_icon} color="#ffffff" />
                  </div>
                ) : (
                  step
                )}
              </div>
              <div
                className={merge(styles.step_label, {
                  [styles.active_step_label]: isActive,
                  [styles.completed_step_label]: isCompletedStepOrConfirmationStep(index),
                })}
                data-testid="stepper-header-step-label"
              >
                {label}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};
